import ImageUploader from '@/components/media/ImageUploader'
import UploadedMediaPreviewTile from '@/components/media/UploadedMediaPreviewTile'
import CreateActionButton from '@/components/patterns/create-action-button'
import { EmptyState } from '@/components/patterns/empty-state'
import FieldInfo from '@/components/form/FieldInfo'
import { Typography } from '@/components/ui/typography'
import { useIsReadOnly } from '@/lib/casl'
import { MediaUploadUsage } from '@/lib/enums/MediaUploadUsage'
import { useFieldContext } from '@/lib/form-contexts'
import { getAssetMedia, mergeEditedMedia } from '@/lib/media-editing'
import { PhotoManagerMode, PhotoManagerSelectionMode } from '@/lib/photo-manager-config'
import { getErrorMessage } from '@/lib/utils'
import { ImagePlus } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'

function getUploadedMedia(response) {
  const media = response?.body?.data ?? response?.body ?? response?.data ?? response

  return media?.id && media?.url ? media : null
}

function appendUniqueMedia(currentMedia, mediaItems) {
  const nextMedia = [...currentMedia]
  let insertedCount = 0
  let skippedCount = 0

  mediaItems.forEach((media) => {
    if (!media?.id || !media?.url) {
      skippedCount += 1
      return
    }

    if (nextMedia.some((item) => Number(item?.id) === Number(media.id))) {
      skippedCount += 1
      return
    }

    nextMedia.push(media)
    insertedCount += 1
  })

  return { nextMedia, insertedCount, skippedCount }
}

export function AppUpdateGalleryInput({
  label = 'Image Gallery',
  description = 'Optional screenshots or visuals that help explain the update.',
}) {
  const field = useFieldContext()
  const { isReadOnly } = useIsReadOnly()
  const uploaderId = useId()
  const [isUploaderOpen, setIsUploaderOpen] = useState(false)
  const gallery = Array.isArray(field.state.value) ? field.state.value : []

  const setGallery = (updater) => {
    field.setValue((currentValue) => {
      const currentGallery = Array.isArray(currentValue) ? currentValue : []

      return updater(currentGallery)
    })
  }

  const addMedia = (mediaItems) => {
    let addResult = { insertedCount: 0, skippedCount: 0 }

    setGallery((currentGallery) => {
      const { nextMedia, insertedCount, skippedCount } = appendUniqueMedia(
        currentGallery,
        mediaItems
      )
      addResult = { insertedCount, skippedCount }

      return insertedCount > 0 ? nextMedia : currentGallery
    })

    return addResult
  }

  const removeMedia = (mediaId) => {
    setGallery((currentGallery) =>
      currentGallery.filter((media) => Number(media?.id) !== Number(mediaId))
    )
  }

  const updateMedia = (mediaId, editedMedia) => {
    setGallery((currentGallery) =>
      currentGallery.map((media) =>
        Number(media?.id) === Number(mediaId) ? mergeEditedMedia(media, editedMedia) : media
      )
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <Typography variant="label">{label}</Typography>
          {description ? (
            <Typography variant="supporting" className="block">
              {description}
            </Typography>
          ) : null}
        </div>
        {!isReadOnly ? (
          <CreateActionButton
            type="button"
            context="inline"
            variant="outline"
            onClick={() => setIsUploaderOpen(true)}
          >
            Add Images
          </CreateActionButton>
        ) : null}
      </div>

      {gallery.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {gallery.map((media) => (
            <UploadedMediaPreviewTile
              key={media.id}
              media={media}
              alt={media.name || 'App update image'}
              caption={media.name}
              onRemove={!isReadOnly ? () => removeMedia(media.id) : null}
              onEditComplete={
                !isReadOnly ? (editedMedia) => updateMedia(media.id, editedMedia) : null
              }
              editUploadUsage={MediaUploadUsage.ImageStandard.Value}
              removeLabel={'Remove ' + (media.name || 'gallery image')}
              wrapperClassName="w-full"
              frameClassName="aspect-video w-full rounded-lg bg-muted"
              imageClassName="h-full w-full rounded-lg object-cover"
              showMetadata
              metadataVisibility="hover"
            />
          ))}
        </div>
      ) : (
        <EmptyState
          size="compact"
          icon={ImagePlus}
          title="No gallery images"
          action={
            !isReadOnly ? (
              <CreateActionButton
                type="button"
                context="inline"
                variant="outline"
                onClick={() => setIsUploaderOpen(true)}
              >
                Add Images
              </CreateActionButton>
            ) : null
          }
        />
      )}

      {!isReadOnly ? (
        <ImageUploader
          id={'app-update-gallery-' + uploaderId}
          open={isUploaderOpen}
          uploadUsage={MediaUploadUsage.ImageStandard.Value}
          photoManagerMode={PhotoManagerMode.UploadOnlyMultiple}
          selectionMode={PhotoManagerSelectionMode.Multiple}
          onRequestClose={() => setIsUploaderOpen(false)}
          onUploadSuccess={(_, response) => {
            const media = getUploadedMedia(response)

            if (media) {
              addMedia([media])
              return
            }

            toast.error('Image uploaded, but the response could not be added to this app update')
          }}
          onUploadError={(_, error) => {
            toast.error('Failed to upload image', {
              description: getErrorMessage(error),
            })
          }}
          onSelectAssets={(assets = []) => addMedia(assets.map((asset) => getAssetMedia(asset)))}
        />
      ) : null}

      <FieldInfo />
    </div>
  )
}
